import { DESTINATION_RECOMMENDATION_CATALOG } from '../data/destinationRecommendationCatalog';
import type {
  DestinationRecommendationCatalogItem,
  RecommendationLevel,
} from '../data/destinationRecommendationCatalog';
import type { WizardData } from '../types/wizard';
import {
  getMustHaveLabel,
  getRegionLabel,
  getTravelStyleLabel,
} from './recommendationPresentation';

export type RecommendationLanguage = 'pt' | 'en';
export type RecommendationRole = 'best_overall' | 'comfortable_simple' | 'inspiring_stretch';
export type RecommendationFit = 'strong' | 'good' | 'stretch';
export type AssessmentStatus = 'aligned' | 'caution' | 'unevaluated';

export interface RecommendationAssessment {
  status: AssessmentStatus;
  label: string;
  detail: string;
}

export interface CanonicalRecommendationProfile {
  language: RecommendationLanguage;
  origin: string | null;
  startDate: string | null;
  endDate: string | null;
  travelMonth: number | null;
  adults: number | null;
  children: number | null;
  totalTravelers: number | null;
  budgetLabel: string | null;
  budgetTier: number | null;
  regions: string[];
  travelStyles: string[];
  rhythm: string | null;
  timePreference: string | null;
  previousTrips: string[];
  mustDoExperiences: string[];
  avoidExperiences: string[];
  lifestyle: string | null;
  ageRange: string | null;
  occupation: string | null;
  unevaluatedFactors: string[];
}

export interface DestinationRecommendation {
  candidateId: string;
  name: string;
  country: string;
  icon: string;
  regions: string[];
  styles: string[];
  minBudgetTier: number;
  maxBudgetTier: number;
  familyFriendly: boolean;
  role: RecommendationRole;
  fit: RecommendationFit;
  reason: string;
  tradeoff: string;
  matchedSignals: string[];
  conflicts: string[];
  unevaluated: string[];
  budgetAssessment: RecommendationAssessment;
  seasonAssessment: RecommendationAssessment;
  flightAssessment: RecommendationAssessment;
  consultantNote: string;
  highlights: string[];
  internalScore: number;
}

interface ExclusionResult {
  excluded: boolean;
  reasons: string[];
}

export interface RecommendationQualificationSummary {
  qualifiedCount: number;
  requestedCount: number;
  exclusionCounts: Record<string, number>;
  explanation: string;
}

const BUDGET_TIERS: Record<string, number> = {
  'Até R$ 5.000': 1,
  'R$ 5.000 a R$ 10.000': 2,
  'R$ 10.000 a R$ 20.000': 3,
  'R$ 20.000 a R$ 35.000': 4,
  'R$ 35.000 a R$ 50.000': 5,
  'R$ 50.000 a R$ 80.000': 6,
  'Acima de R$ 80.000': 7,
};

const ROLE_LABELS: Record<RecommendationLanguage, Record<RecommendationRole, string>> = {
  pt: {
    best_overall: 'Melhor opção',
    comfortable_simple: 'Alternativa forte',
    inspiring_stretch: 'Opção com ressalvas',
  },
  en: {
    best_overall: 'Best match',
    comfortable_simple: 'Strong alternative',
    inspiring_stretch: 'Stretch option',
  },
};

const AVOIDANCE_ALIASES: Record<string, string> = {
  multidoes: 'crowds',
  'crowds packed places': 'crowds',
  crowds: 'crowds',
  voos_longos: 'long_flights',
  'very long flights': 'long_flights',
  atividades_fisicas: 'physical_intensity',
  'intense physical activities': 'physical_intensity',
  calor_extremo: 'extreme_heat',
  'extreme heat': 'extreme_heat',
  frio_extremo: 'extreme_cold',
  'extreme cold': 'extreme_cold',
  vida_noturna: 'nightlife',
  'heavy nightlife': 'nightlife',
  comida_exotica: 'exotic_food',
  'very exotic food': 'exotic_food',
  areas_urbanas: 'large_urban',
  'large urban centers': 'large_urban',
  isolamento: 'isolation',
  'very isolated places': 'isolation',
  barulho: 'noise',
  'noisy environments': 'noise',
  transporte_publico: 'complex_transit',
  'complex public transport': 'complex_transit',
  idioma_dificil: 'language_barrier',
  'language barriers': 'language_barrier',
};

// Canonical quiz IDs mapped to deterministic catalog capabilities. Every selected
// must-have must match at least one capability keyword for a candidate to qualify.
const MUST_HAVE_CAPABILITY_ALIASES: Record<string, string[]> = {
  gastronomia: ['gastronomia', 'mercados', 'comida'],
  cultura: ['cultura', 'historia', 'templos', 'arquitetura', 'arqueologia', 'patrimonio'],
  natureza: ['natureza', 'paisagem', 'praia', 'lagoa', 'montanha', 'rio', 'deserto', 'wadi', 'recife'],
  aventura: ['aventura', 'travessia', 'mergulho', 'cenote', 'trilha', 'montanha', 'deserto', 'balao'],
  vida_noturna: ['vida noturna', 'bares', 'clubes', 'shows'],
  compras: ['compras', 'mercados', 'boutiques'],
  relaxamento: ['descanso', 'spa', 'praia', 'resort', 'bem estar'],
  fotografia: ['paisagem', 'arquitetura', 'lagoa', 'praia', 'templos', 'deserto', 'mirante'],
  arte: ['arte', 'arquitetura', 'artesanato', 'museus'],
  espiritualidade: ['templos', 'retiro', 'espiritualidade'],
  interacao_local: ['comunidades locais', 'cultura local', 'mercados', 'artesanato'],
  vinhos: ['vinho', 'vinicola', 'degustacao'],
};

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function localize(language: RecommendationLanguage, pt: string, en: string): string {
  return language === 'en' ? en : pt;
}

function parseOptionalCount(value: string | undefined): number | null {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parseTravelMonth(value: string | undefined): number | null {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const month = Number.parseInt(match[2], 10);
    return month >= 1 && month <= 12 ? month : null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getUTCMonth() + 1;
}

export function getRecommendationBudgetTier(value: string | undefined): number | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (BUDGET_TIERS[raw]) return BUDGET_TIERS[raw];

  const normalized = normalizeText(raw);
  const matchingEntry = Object.entries(BUDGET_TIERS).find(([label]) => normalizeText(label) === normalized);
  return matchingEntry?.[1] ?? null;
}

export function buildCanonicalRecommendationProfile(
  data: WizardData,
  language: RecommendationLanguage,
): CanonicalRecommendationProfile {
  const adults = parseOptionalCount(data.numeroAdultos);
  const children = parseOptionalCount(data.numeroCriancas);
  const totalTravelers = adults == null && children == null ? null : (adults ?? 0) + (children ?? 0);
  const travelStyles = Array.from(
    new Set([...(data.travelStyles ?? []), data.travelStyle ?? ''].filter(Boolean)),
  );
  const unevaluatedFactors: string[] = [];

  if (!data.origem?.trim()) unevaluatedFactors.push('origin');
  if (!data.dataInicio) unevaluatedFactors.push('travel_month');
  if (adults == null) unevaluatedFactors.push('adults');
  if (children == null) unevaluatedFactors.push('children');
  if (!data.orcamento) unevaluatedFactors.push('budget');
  if (!(data.regions ?? []).length) unevaluatedFactors.push('regions');
  if (!travelStyles.length) unevaluatedFactors.push('travel_styles');
  if (!data.rhythm) unevaluatedFactors.push('rhythm');
  if (!data.timePreference) unevaluatedFactors.push('time_preference');

  return {
    language,
    origin: data.origem?.trim() || null,
    startDate: data.dataInicio || null,
    endDate: data.dataFim || null,
    travelMonth: parseTravelMonth(data.dataInicio),
    adults,
    children,
    totalTravelers,
    budgetLabel: data.orcamento || null,
    budgetTier: getRecommendationBudgetTier(data.orcamento),
    regions: [...(data.regions ?? [])],
    travelStyles,
    rhythm: data.rhythm || null,
    timePreference: data.timePreference || null,
    previousTrips: [...(data.previousTrips ?? [])],
    mustDoExperiences: [...(data.mustDoExperiences ?? [])],
    avoidExperiences: [...(data.avoidExperiences ?? [])],
    lifestyle: data.lifestyle || null,
    ageRange: data.ageRange || null,
    occupation: data.occupation || null,
    unevaluatedFactors,
  };
}

function normalizedAvoidances(profile: CanonicalRecommendationProfile): Set<string> {
  return new Set(
    profile.avoidExperiences
      .map((value) => {
        const canonicalValue = value.trim().toLowerCase();
        const normalizedValue = normalizeText(value);
        return AVOIDANCE_ALIASES[canonicalValue] ?? AVOIDANCE_ALIASES[normalizedValue] ?? normalizedValue;
      })
      .filter(Boolean),
  );
}

function getUnsupportedMustHaves(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): string[] {
  const capabilities = candidate.supportedExperiences.map(normalizeText);
  return profile.mustDoExperiences.filter((mustHave) => {
    const canonicalMustHave = mustHave.trim().toLowerCase();
    const normalizedMustHave = normalizeText(mustHave);
    const aliases =
      MUST_HAVE_CAPABILITY_ALIASES[canonicalMustHave] ??
      MUST_HAVE_CAPABILITY_ALIASES[normalizedMustHave] ??
      [normalizedMustHave];
    return !aliases.some((alias) => {
      const normalizedAlias = normalizeText(alias);
      return capabilities.some(
        (capability) => capability.includes(normalizedAlias) || normalizedAlias.includes(capability),
      );
    });
  });
}

function isFamilyTravelProfile(profile: CanonicalRecommendationProfile): boolean {
  return (profile.children ?? 0) > 0 || profile.travelStyles.some((style) => normalizeText(style) === 'familia');
}

function includesPreviousTrip(profile: CanonicalRecommendationProfile, candidate: DestinationRecommendationCatalogItem): boolean {
  const candidateTokens = [candidate.name, candidate.country]
    .flatMap((value) => normalizeText(value).split(' '))
    .filter((value) => value.length >= 4);

  return profile.previousTrips.some((trip) => {
    const normalizedTrip = normalizeText(trip);
    return candidateTokens.some((token) => normalizedTrip.includes(token));
  });
}

function shouldExclude(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): ExclusionResult {
  const avoidances = normalizedAvoidances(profile);
  const reasons: string[] = [];

  if (profile.regions.length > 0 && !candidate.regions.some((region) => profile.regions.includes(region))) {
    reasons.push('region_mismatch');
  }
  getUnsupportedMustHaves(profile, candidate).forEach((mustHave) => reasons.push(`must_have:${mustHave}`));
  if (includesPreviousTrip(profile, candidate)) reasons.push('already_visited');
  if (avoidances.has('crowds') && candidate.crowdLevel === 'high') reasons.push('crowds');
  if (avoidances.has('large_urban') && candidate.largeUrban) reasons.push('large_urban');
  if (avoidances.has('noise') && candidate.noiseLevel === 'high') reasons.push('noise');
  if (avoidances.has('nightlife') && candidate.nightlifeCore) reasons.push('nightlife');
  if (avoidances.has('complex_transit') && candidate.transitComplexity === 'high') reasons.push('complex_transit');
  if (avoidances.has('physical_intensity') && candidate.physicalIntensity === 'high') reasons.push('physical_intensity');
  if (avoidances.has('isolation') && candidate.isolationLevel === 'high') reasons.push('isolation');
  if (avoidances.has('exotic_food') && candidate.exoticFoodDependency === 'high') reasons.push('exotic_food');
  if (avoidances.has('language_barrier') && candidate.englishOrPortugueseAccess === 'low') reasons.push('language_barrier');
  if (
    avoidances.has('extreme_heat') &&
    profile.travelMonth != null &&
    candidate.extremeHeatRiskMonths.includes(profile.travelMonth)
  ) reasons.push('extreme_heat');
  if (
    avoidances.has('extreme_cold') &&
    profile.travelMonth != null &&
    candidate.extremeColdRiskMonths.includes(profile.travelMonth)
  ) reasons.push('extreme_cold');
  if (profile.budgetTier != null && candidate.minBudgetTier - profile.budgetTier >= 2) reasons.push('budget_gap');

  return { excluded: reasons.length > 0, reasons };
}

function levelScore(level: RecommendationLevel): number {
  return level === 'high' ? 3 : level === 'medium' ? 2 : 1;
}

function buildBudgetAssessment(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): RecommendationAssessment {
  const { language } = profile;
  if (profile.budgetTier == null) {
    return {
      status: 'unevaluated',
      label: localize(language, 'Orçamento não avaliado', 'Budget not evaluated'),
      detail: localize(language, 'A faixa de investimento não foi informada.', 'No investment range was provided.'),
    };
  }
  if (profile.budgetTier < candidate.minBudgetTier) {
    return {
      status: 'caution',
      label: localize(language, 'Exige ajuste de escopo', 'Scope adjustment needed'),
      detail: localize(
        language,
        'A consultoria deve ajustar hotel, duração ou experiências para respeitar a faixa informada.',
        'The consultant should adjust hotel, duration or experiences to respect the selected range.',
      ),
    };
  }
  return {
    status: 'aligned',
    label: localize(language, 'Compatível com a faixa', 'Aligned with the range'),
    detail: localize(
      language,
      'A proposta pode ser estruturada dentro da faixa, sujeita a datas e disponibilidade.',
      'The trip can be structured within the range, subject to dates and availability.',
    ),
  };
}

function buildSeasonAssessment(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): RecommendationAssessment {
  const { language, travelMonth } = profile;
  if (travelMonth == null) {
    return {
      status: 'unevaluated',
      label: localize(language, 'Época não avaliada', 'Season not evaluated'),
      detail: localize(language, 'As datas ainda não permitem avaliar a sazonalidade.', 'The dates do not yet allow a season check.'),
    };
  }
  if (candidate.extremeHeatRiskMonths.includes(travelMonth) || candidate.extremeColdRiskMonths.includes(travelMonth)) {
    return {
      status: 'caution',
      label: localize(language, 'Atenção à época', 'Seasonal caution'),
      detail: localize(
        language,
        'O mês escolhido tem uma condição climática relevante para esta rota.',
        'The selected month has a relevant climate condition for this route.',
      ),
    };
  }
  return {
    status: 'aligned',
    label: localize(language, 'Sem conflito sazonal conhecido', 'No known seasonal conflict'),
    detail: localize(
      language,
      'O catálogo não registra conflito climático extremo para o mês selecionado.',
      'The catalog records no extreme climate conflict for the selected month.',
    ),
  };
}

function buildFlightAssessment(profile: CanonicalRecommendationProfile): RecommendationAssessment {
  const { language } = profile;
  return {
    status: 'unevaluated',
    label: localize(language, 'Voos não avaliados', 'Flights not evaluated'),
    detail: profile.origin
      ? localize(
          language,
          'A recomendação não usa disponibilidade ou duração de voo em tempo real. A consultoria deve confirmar a rota.',
          'The recommendation does not use real-time flight duration or availability. The consultant must confirm the route.',
        )
      : localize(
          language,
          'Sem origem informada, duração, conexões e facilidade aérea permanecem não avaliadas.',
          'Without an origin, flight duration, connections and route convenience remain unevaluated.',
        ),
  };
}

function buildMatchSignals(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): string[] {
  const language = profile.language;
  const signals: string[] = [];
  const styleMatches = profile.travelStyles.filter((style) => candidate.styles.includes(style));
  const regionMatches = profile.regions.filter((region) => candidate.regions.includes(region));
  const mustDoText = profile.mustDoExperiences.map(normalizeText).join(' ');
  const experienceMatches = candidate.supportedExperiences.filter((experience) =>
    mustDoText.includes(normalizeText(experience)),
  );

  if (regionMatches.length) {
    signals.push(localize(
      language,
      `Região: ${regionMatches.map((region) => getRegionLabel(region, 'pt')).join(', ')}`,
      `Region: ${regionMatches.map((region) => getRegionLabel(region, 'en')).join(', ')}`,
    ));
  }
  if (styleMatches.length) {
    signals.push(localize(
      language,
      `Estilo: ${styleMatches.map((style) => getTravelStyleLabel(style, 'pt')).join(', ')}`,
      `Style: ${styleMatches.map((style) => getTravelStyleLabel(style, 'en')).join(', ')}`,
    ));
  }
  if (profile.mustDoExperiences.length > 0) {
    signals.push(localize(
      language,
      `Essenciais: ${profile.mustDoExperiences.slice(0, 3).map((item) => getMustHaveLabel(item, 'pt')).join(', ')}`,
      `Must-haves: ${profile.mustDoExperiences.slice(0, 3).map((item) => getMustHaveLabel(item, 'en')).join(', ')}`,
    ));
  } else if (experienceMatches.length) {
    signals.push(localize(language, `Interesses: ${experienceMatches.slice(0, 2).join(', ')}`, `Interests: ${experienceMatches.slice(0, 2).join(', ')}`));
  }
  if (isFamilyTravelProfile(profile) && candidate.familyFriendly) {
    signals.push(localize(language, 'Estrutura compatível com viagem em família', 'Infrastructure suited to family travel'));
  }
  if (candidate.simplicity === 'high') signals.push(localize(language, 'Logística mais simples', 'Simpler logistics'));

  return signals.slice(0, 4);
}

function buildSoftConflicts(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
): string[] {
  const language = profile.language;
  const avoidances = normalizedAvoidances(profile);
  const conflicts: string[] = [];

  if (avoidances.has('crowds') && candidate.crowdLevel === 'medium') {
    conflicts.push(localize(language, 'Pode exigir horários alternativos para evitar movimento.', 'May require off-peak timing to avoid crowds.'));
  }
  if (avoidances.has('noise') && candidate.noiseLevel === 'medium') {
    conflicts.push(localize(language, 'A escolha do bairro/hotel deve priorizar tranquilidade.', 'Hotel and neighborhood selection should prioritize calm.'));
  }
  if (avoidances.has('complex_transit') && candidate.transitComplexity === 'medium') {
    conflicts.push(localize(language, 'Alguns deslocamentos precisam de planejamento.', 'Some transfers need advance planning.'));
  }
  if (profile.budgetTier != null && profile.budgetTier < candidate.minBudgetTier) {
    conflicts.push(localize(language, 'A faixa informada pede ajuste de escopo.', 'The selected range calls for a scope adjustment.'));
  }
  if (profile.rhythm && normalizeText(profile.rhythm).includes('intens') && candidate.physicalIntensity === 'low') {
    conflicts.push(localize(
      language,
      'O destino tem ritmo naturalmente mais lento; um roteiro intenso exige mais densidade e deslocamentos planejados.',
      'The destination has a naturally slower rhythm; an intense itinerary requires added density and planned movement.',
    ));
  }
  return conflicts;
}

function scoreCandidate(
  profile: CanonicalRecommendationProfile,
  candidate: DestinationRecommendationCatalogItem,
  conflicts: string[],
): number {
  let score = 0;
  score += profile.regions.filter((region) => candidate.regions.includes(region)).length * 18;
  score += profile.travelStyles.filter((style) => candidate.styles.includes(style)).length * 12;
  score += profile.mustDoExperiences.length * 8;
  if (isFamilyTravelProfile(profile) && candidate.familyFriendly) score += 10;
  if (profile.rhythm && normalizeText(profile.rhythm).includes('relax') && candidate.simplicity === 'high') score += 8;
  if (profile.rhythm && normalizeText(profile.rhythm).includes('intens') && candidate.physicalIntensity !== 'low') score += 5;
  if (profile.timePreference && normalizeText(profile.timePreference).includes('manha') && candidate.nightlifeCore === false) score += 3;
  if (profile.budgetTier != null && profile.budgetTier >= candidate.minBudgetTier && profile.budgetTier <= candidate.maxBudgetTier) score += 10;
  score += levelScore(candidate.simplicity) * 2;
  score -= conflicts.length * 14;
  return score;
}

function getFit(score: number, conflicts: string[]): RecommendationFit {
  if (conflicts.length > 0 || score < 35) return 'stretch';
  if (score >= 58) return 'strong';
  return 'good';
}

function getUnevaluatedLabels(profile: CanonicalRecommendationProfile): string[] {
  const labels: Record<string, { pt: string; en: string }> = {
    origin: { pt: 'Origem e facilidade de voo', en: 'Origin and flight convenience' },
    travel_month: { pt: 'Sazonalidade', en: 'Seasonality' },
    adults: { pt: 'Quantidade de adultos', en: 'Adult count' },
    children: { pt: 'Quantidade de crianças', en: 'Child count' },
    budget: { pt: 'Compatibilidade de orçamento', en: 'Budget compatibility' },
    regions: { pt: 'Preferência regional', en: 'Regional preference' },
    travel_styles: { pt: 'Estilo de viagem', en: 'Travel style' },
    rhythm: { pt: 'Ritmo de viagem', en: 'Travel pace' },
    time_preference: { pt: 'Preferência de horário', en: 'Time preference' },
  };
  return profile.unevaluatedFactors.map((factor) => labels[factor]?.[profile.language] ?? factor);
}

function selectRecommendations(
  ranked: DestinationRecommendation[],
): DestinationRecommendation[] {
  if (ranked.length === 0) return [];
  const roles: RecommendationRole[] = ['best_overall', 'comfortable_simple', 'inspiring_stretch'];
  return ranked.slice(0, 3).map((recommendation, index) => ({
    ...recommendation,
    role: roles[index],
  }));
}

export function rankDestinationRecommendations(
  profile: CanonicalRecommendationProfile,
  catalog: DestinationRecommendationCatalogItem[] = DESTINATION_RECOMMENDATION_CATALOG,
): DestinationRecommendation[] {
  const ranked = catalog
    .filter((candidate) => !shouldExclude(profile, candidate).excluded)
    .map((candidate): DestinationRecommendation => {
      const conflicts = buildSoftConflicts(profile, candidate);
      const internalScore = scoreCandidate(profile, candidate, conflicts);
      const budgetAssessment = buildBudgetAssessment(profile, candidate);
      const seasonAssessment = buildSeasonAssessment(profile, candidate);
      const flightAssessment = buildFlightAssessment(profile);
      const matchedSignals = buildMatchSignals(profile, candidate);
      const unevaluated = getUnevaluatedLabels(profile);
      const consultantNote = localize(
        profile.language,
        'Confirmar voos, disponibilidade, hotel e ritmo final antes da proposta.',
        'Confirm flights, availability, hotel and final pacing before the proposal.',
      );

      return {
        candidateId: candidate.candidateId,
        name: candidate.name,
        country: candidate.country,
        icon: candidate.icon,
        regions: candidate.regions,
        styles: candidate.styles,
        minBudgetTier: candidate.minBudgetTier,
        maxBudgetTier: candidate.maxBudgetTier,
        familyFriendly: candidate.familyFriendly,
        role: 'best_overall',
        fit: getFit(internalScore, conflicts),
        reason: candidate.reason[profile.language],
        tradeoff: candidate.tradeoff[profile.language],
        matchedSignals,
        conflicts,
        unevaluated,
        budgetAssessment,
        seasonAssessment,
        flightAssessment,
        consultantNote,
        highlights: matchedSignals.slice(0, 3),
        internalScore,
      };
    })
    .sort((a, b) => b.internalScore - a.internalScore || a.name.localeCompare(b.name));

  return selectRecommendations(ranked).map((recommendation) => ({
    ...recommendation,
    highlights: [ROLE_LABELS[profile.language][recommendation.role], ...recommendation.highlights].slice(0, 3),
  }));
}

export function getRecommendationRoleLabel(role: RecommendationRole, language: RecommendationLanguage): string {
  return ROLE_LABELS[language][role];
}

export function getRecommendationQualificationSummary(
  profile: CanonicalRecommendationProfile,
  catalog: DestinationRecommendationCatalogItem[] = DESTINATION_RECOMMENDATION_CATALOG,
): RecommendationQualificationSummary {
  const exclusionCounts: Record<string, number> = {};
  let qualifiedCount = 0;

  catalog.forEach((candidate) => {
    const result = shouldExclude(profile, candidate);
    if (!result.excluded) {
      qualifiedCount += 1;
      return;
    }
    result.reasons.forEach((reason) => {
      const key = reason.startsWith('must_have:') ? 'must_haves' : reason;
      exclusionCounts[key] = (exclusionCounts[key] ?? 0) + 1;
    });
  });

  const requestedCount = 3;
  const reasonLabels: Record<string, { pt: string; en: string }> = {
    region_mismatch: { pt: 'região selecionada', en: 'selected region' },
    already_visited: { pt: 'destinos já visitados', en: 'previously visited destinations' },
    must_haves: { pt: 'experiências obrigatórias', en: 'must-have experiences' },
    crowds: { pt: 'preferência por evitar multidões', en: 'crowd avoidance' },
    large_urban: { pt: 'preferência por evitar grandes centros', en: 'large-city avoidance' },
    extreme_heat: { pt: 'calor extremo nas datas', en: 'extreme heat on the selected dates' },
    extreme_cold: { pt: 'frio extremo nas datas', en: 'extreme cold on the selected dates' },
    budget_gap: { pt: 'diferença de orçamento', en: 'budget gap' },
  };
  const leadingReasons = Object.entries(exclusionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([reason]) => reasonLabels[reason]?.[profile.language] ?? reason.replace(/_/g, ' '));
  const explanation = qualifiedCount >= requestedCount
    ? ''
    : localize(
        profile.language,
        `Encontramos ${qualifiedCount} ${qualifiedCount === 1 ? 'opção que atende' : 'opções que atendem'} a todos os critérios obrigatórios${leadingReasons.length ? `. Os principais filtros foram: ${leadingReasons.join(', ')}.` : '.'}`,
        `We found ${qualifiedCount} ${qualifiedCount === 1 ? 'option that meets' : 'options that meet'} every required criterion${leadingReasons.length ? `. The main filters were: ${leadingReasons.join(', ')}.` : '.'}`,
      );

  return { qualifiedCount, requestedCount, exclusionCounts, explanation };
}

export function getRecommendationFitLabel(fit: RecommendationFit, language: RecommendationLanguage): string {
  const labels: Record<RecommendationLanguage, Record<RecommendationFit, string>> = {
    pt: { strong: 'Forte', good: 'Bom', stretch: 'Com ressalvas' },
    en: { strong: 'Strong', good: 'Good', stretch: 'Stretch' },
  };
  return labels[language][fit];
}
